// @ts-ignore - Temporary ignore for React 19 + Radix UI v2 compatibility issues
import { forwardRef, createContext, useContext } from 'react';
import * as Toast from '@radix-ui/react-toast';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../utils/cn';

const toastVariants = cva(
  'group pointer-events-auto relative flex w-full items-center justify-between space-x-4 overflow-hidden rounded-md border p-6 pr-8 shadow-lg transition-all data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-top-full data-[state=open]:sm:slide-in-from-bottom-full',
  {
    variants: {
      variant: {
        default: 'border bg-white dark:bg-gray-800 text-gray-950 dark:text-gray-50',
        destructive:
          'destructive border-red-500 bg-red-500 text-gray-50',
        success:
          'border-green-500 bg-green-500 text-gray-50',
        warning:
          'border-yellow-500 bg-yellow-500 text-gray-900',
        info:
          'border-blue-500 bg-blue-500 text-gray-50',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

// Toast Provider context
const ToastContext = createContext<{
  addToast: (toast: ToastData) => void;
  removeToast: (id: string) => void;
} | null>(null);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export interface ToastData {
  id?: string;
  title?: string;
  description?: string;
  variant?: 'default' | 'destructive' | 'success' | 'warning' | 'info';
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

const ToastProvider: React.FC<React.ComponentProps<typeof Toast.Provider>> = Toast.Provider;

const ToastViewport = forwardRef<
  HTMLOListElement,
  React.HTMLAttributes<HTMLOListElement>
>(({ className, ...props }, ref) => (
  <Toast.Viewport
    ref={ref as any}
    asChild
    {...(props as any)}
  >
    <ol
      className={cn(
        'fixed top-0 z-[100] flex max-h-screen w-full flex-col-reverse p-4 sm:bottom-0 sm:right-0 sm:top-auto sm:flex-col md:max-w-[420px]',
        className
      )}
      {...props}
    />
  </Toast.Viewport>
));

ToastViewport.displayName = Toast.Viewport.displayName;

const ToastComponent = forwardRef<
  HTMLLIElement,
  React.HTMLAttributes<HTMLLIElement> &
    VariantProps<typeof toastVariants> & {
    duration?: number;
  }
>(({ className, variant, duration, ...props }, ref) => {
  return (
    <Toast.Root
      ref={ref as any}
      duration={duration}
      asChild
      {...(props as any)}
    >
      <li
        className={cn(toastVariants({ variant }), className)}
        {...props}
      />
    </Toast.Root>
  );
});

ToastComponent.displayName = Toast.Root.displayName;

const ToastAction = forwardRef<
  HTMLButtonElement,
  React.HTMLAttributes<HTMLButtonElement> & {
    altText: string;
  }
>(({ className, altText, ...props }, ref) => (
  <Toast.Action
    ref={ref as any}
    altText={altText}
    asChild
    {...(props as any)}
  >
    <button
      className={cn(
        'inline-flex h-8 shrink-0 items-center justify-center rounded-md border bg-transparent px-3 text-xs font-medium ring-offset-background transition-colors hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 group-[.destructive]:border-red-600 group-[.destructive]:hover:border-red-600 group-[.destructive]:hover:bg-red-600 group-[.destructive]:hover:text-red-50 group-[.destructive]:focus:ring-red-600',
        className
      )}
      {...props}
    />
  </Toast.Action>
));

ToastAction.displayName = Toast.Action.displayName;

const ToastClose = forwardRef<
  HTMLButtonElement,
  React.HTMLAttributes<HTMLButtonElement>
>(({ className, ...props }, ref) => (
  <Toast.Close
    ref={ref as any}
    asChild
    {...(props as any)}
  >
    <button
      className={cn(
        'absolute right-2 top-2 rounded-md p-1 text-gray-950/50 opacity-0 transition-opacity hover:text-gray-950 focus:opacity-100 focus:outline-none focus:ring-2 group-hover:opacity-100 group-[.destructive]:text-red-300 group-[.destructive]:hover:text-red-50 group-[.destructive]:focus:ring-red-600',
        className
      )}
      toast-close=""
      {...props}
    >
      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  </Toast.Close>
));

ToastClose.displayName = Toast.Close.displayName;

const ToastTitle = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <Toast.Title
    ref={ref as any}
    asChild
    {...(props as any)}
  >
    <div
      className={cn('text-sm font-semibold', className)}
      {...props}
    />
  </Toast.Title>
));

ToastTitle.displayName = Toast.Title.displayName;

const ToastDescription = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <Toast.Description
    ref={ref as any}
    asChild
    {...(props as any)}
  >
    <div
      className={cn('text-sm opacity-90', className)}
      {...props}
    />
  </Toast.Description>
));

ToastDescription.displayName = Toast.Description.displayName;

// Simple toast function for quick use
export const toast = {
  success: (message: string, title?: string) => ({
    variant: 'success' as const,
    title: title || 'Success',
    description: message,
  }),
  error: (message: string, title?: string) => ({
    variant: 'destructive' as const,
    title: title || 'Error',
    description: message,
  }),
  warning: (message: string, title?: string) => ({
    variant: 'warning' as const,
    title: title || 'Warning',
    description: message,
  }),
  info: (message: string, title?: string) => ({
    variant: 'info' as const,
    title: title || 'Info',
    description: message,
  }),
  default: (message: string, title?: string) => ({
    variant: 'default' as const,
    title: title || 'Notification',
    description: message,
  }),
};

// Complete Toast component for easy use
export interface SimpleToastProps {
  toast: ToastData;
  onClose: () => void;
}

const SimpleToast = ({ toast: toastData, onClose }: SimpleToastProps) => {
  return (
    <ToastComponent variant={toastData.variant} duration={toastData.duration || 5000}>
      <div className="grid gap-1">
        {toastData.title && <ToastTitle>{toastData.title}</ToastTitle>}
        {toastData.description && (
          <ToastDescription>{toastData.description}</ToastDescription>
        )}
      </div>
      {toastData.action && (
        <ToastAction 
          onClick={toastData.action.onClick}
          altText={toastData.action.label}
        >
          {toastData.action.label}
        </ToastAction>
      )}
      <ToastClose onClick={onClose} />
    </ToastComponent>
  );
};

export {
  ToastProvider,
  ToastViewport,
  ToastComponent as Toast,
  ToastAction,
  ToastClose,
  ToastTitle,
  ToastDescription,
  SimpleToast,
  toastVariants,
};
